import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Crown, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import UpgradeDialog from "./UpgradeDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth } from "../context/AuthContext";

export default function Navbar({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const isPremium = user?.plan === "premium";

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <nav className="glass sticky top-0 z-50 border-b border-slate-800 px-4 sm:px-6 py-3 flex items-center justify-between">
      <div
        data-testid="brand-home-link"
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-2 font-display font-extrabold text-lg cursor-pointer"
      >
        <img src="/swotkit-logo.png" alt="SwotKit" className="h-9 w-9 rounded-lg bg-white p-0.5" />
        SwotKit
      </div>

      <div className="flex items-center gap-3">
        {children}
        {user && (isPremium ? (
          <Badge data-testid="premium-badge" className="bg-gradient-to-r from-orange-400/20 to-rose-500/20 text-orange-300 border-orange-500/40 hidden sm:flex"><Crown className="h-3 w-3 mr-1" /> Premium</Badge>
        ) : (
          <Button data-testid="upgrade-nav-button" size="sm" onClick={() => setShowUpgrade(true)} className="cta-btn rounded-full">
            <Sparkles className="h-4 w-4 mr-1" /> Upgrade
          </Button>
        ))}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-avatar-button" className="rounded-full ring-2 ring-transparent hover:ring-indigo-500/60 transition">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user.picture} alt={user.name} />
                  <AvatarFallback className="bg-indigo-600 text-white">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700 text-slate-100">
              <DropdownMenuLabel>
                <div className="font-semibold">{user.name}</div>
                <div className="text-xs text-slate-400 font-normal">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem data-testid="logout-button" onClick={doLogout} className="cursor-pointer focus:bg-slate-800">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </nav>
  );
}
